import { trackingPageOrderSchema } from "@redotech/redo-model/order-tracking/tracking-page-order-definition";
import * as z from "zod";
import { SingleOrderAuthStatus } from "./single-order-auth";

/**
 * Public, token-less counterpart to `getTrackingPageOrder`. Authenticates
 * in-band from login `credentials` (email/zip + order/tracking number) and
 * returns the tracking page order in a single request, collapsing the
 * `singleOrderAuth` → `getTrackingPageOrder` round trip the order-tracking
 * lookup used to pay before rendering.
 *
 * Ownership is verified internally — the same checks `singleOrderAuth` performs,
 * including order-selection disambiguation. When the credentials match more than
 * one order it returns `ORDER_SELECTION_REQUIRED` with the candidate orders so
 * the caller can disambiguate, exactly like `singleOrderAuth`. Unlike
 * `singleOrderAuth` it does NOT mint a `customerAuthToken` or upsert a customer —
 * the read-only tracking page does not need either.
 */
export const getTrackingPageOrderWithCredentials = {
  name: "getTrackingPageOrderWithCredentials" as const,
  input: z.object({
    credentials: z.object({
      emailOrZip: z.string(),
      orderIdTrackingNumber: z.string(),
    }),
  }),
  output: z.union([
    z.object({
      trackingPageOrder: trackingPageOrderSchema.nullable(),
      jsMapsApiEnabled: z.boolean(),
    }),
    z.object({
      status: z.literal(SingleOrderAuthStatus.ORDER_SELECTION_REQUIRED),
      orders: z.array(
        z.object({ shopifyId: z.string(), orderName: z.string() }),
      ),
    }),
  ]),
};

export type GetTrackingPageOrderWithCredentialsOutput = z.infer<
  (typeof getTrackingPageOrderWithCredentials)["output"]
>;

export type OrderSelectionRequiredResponse = Extract<
  GetTrackingPageOrderWithCredentialsOutput,
  { status: typeof SingleOrderAuthStatus.ORDER_SELECTION_REQUIRED }
>;

/**
 * Type guard for the `ORDER_SELECTION_REQUIRED` arm. Callers must check this and
 * render the disambiguation UI before reading the order.
 */
export function isOrderSelectionRequired(
  response: GetTrackingPageOrderWithCredentialsOutput | undefined,
): response is OrderSelectionRequiredResponse {
  return (
    !!response &&
    "status" in response &&
    response.status === SingleOrderAuthStatus.ORDER_SELECTION_REQUIRED
  );
}
